import {
  type MouseEvent,
  type PointerEvent,
  type ReactNode,
  useId,
  useState,
} from 'react'
import cx from 'classnames'
import { Button, RawButton } from '@/components/Button'
import { DropdownItem, DropdownItemList } from '@/components/Dropdown'
import { Icon } from '@/components/Icon'
import styles from './SavedFilterEditor.module.css'

export interface SavedFilterEditorField {
  key: string
  title: string
}

export interface SavedFilterEditorRule {
  key: string
  field: string
  valueText: string
  isEditing: boolean
  /**
   * Picks the value, in the panel beside the editor while editing.
   */
  valueEditor: ReactNode
}

export interface SavedFilterEditorProps {
  name: string
  nameMaxLength: number
  onNameChange: (name: string) => void
  /**
   * The default the tab starts from, shown as a rule that can't change.
   */
  startsFrom: string | null
  rules: SavedFilterEditorRule[]
  /**
   * Filters that can still be added.
   */
  addableFields: SavedFilterEditorField[]
  onAdd: (key: string) => void
  onChangeField: (key: string, toKey: string) => void
  onRemove: (key: string) => void
  onEditValue: (key: string) => void
  onStopEditingValues: () => void
  /**
   * How many jobs the tab shows, or `null` when that isn't known.
   */
  jobCount: number | null
  canSave: boolean
  onCancel: () => void
  onSave: () => void
}

type Side = 'left' | 'right'

const panelWidth = 260

/**
 * Edits one of the viewer's own tabs under the tab. Fields and values are
 * picked in a panel beside the editor, so the editor itself never grows.
 * The list behind it shows the filters as they change.
 */
export function SavedFilterEditor({
  name,
  nameMaxLength,
  onNameChange,
  startsFrom,
  rules,
  addableFields,
  onAdd,
  onChangeField,
  onRemove,
  onEditValue,
  onStopEditingValues,
  jobCount,
  canSave,
  onCancel,
  onSave,
}: SavedFilterEditorProps) {
  const nameId = useId()
  const [fieldPanelKey, setFieldPanelKey] = useState<string | null>(null)
  const [isAdding, setIsAdding] = useState(false)
  const [side, setSide] = useState<Side>('right')

  // Measured as a panel opens, since the editor is only placed under its
  // tab after it first renders.
  const placePanel = (e: MouseEvent<HTMLElement>) => {
    const editor = e.currentTarget.closest('form')
    if (editor) {
      const room = window.innerWidth - editor.getBoundingClientRect().right
      setSide(room >= panelWidth + 16 ? 'right' : 'left')
    }
  }

  const closePanelsOutside = (e: PointerEvent<HTMLFormElement>) => {
    const target = e.target as Element
    if (!target.closest('[data-editor-panel], [data-editor-panel-trigger]')) {
      setFieldPanelKey(null)
      setIsAdding(false)
      onStopEditingValues()
    }
  }

  const fieldsFor = (key: string) =>
    addableFields.filter((field) => field.key !== key)

  return (
    <form
      className={styles.root}
      onPointerDown={closePanelsOutside}
      onSubmit={(e) => {
        e.preventDefault()
        onSave()
      }}
      data-test="job-list-saved-filter-editor"
    >
      <div className={styles.body}>
        <div className={styles.group}>
          <label className={styles.label} htmlFor={nameId}>
            Name
          </label>
          <input
            id={nameId}
            className={styles.name}
            value={name}
            maxLength={nameMaxLength}
            onChange={(e) => onNameChange(e.target.value)}
            autoFocus
            data-test="job-list-saved-filter-name"
          />
        </div>
        <div className={styles.group}>
          <span className={styles.label}>Show jobs where</span>
          {startsFrom && (
            <>
              <div
                className={styles.rule}
                data-test="job-list-saved-filter-base"
              >
                <span className={cx(styles.select, styles.locked)}>
                  <Icon name="lock" size={12} />
                  Starts from
                </span>
                <span
                  className={cx(styles.select, styles.locked, styles.value)}
                >
                  {startsFrom}
                </span>
              </div>
              <p className={styles.hint}>
                Follows the default tab, so it stays right if that tab changes.
                Add filters to narrow it.
              </p>
            </>
          )}
          {rules.map((rule) => (
            <div
              key={rule.key}
              className={styles.rule}
              data-test={`job-list-saved-filter-rule-${rule.key}`}
            >
              <RawButton
                className={cx(
                  styles.select,
                  fieldPanelKey === rule.key && styles.selectOpen,
                )}
                aria-expanded={fieldPanelKey === rule.key}
                onPress={(e) => {
                  placePanel(e)
                  onStopEditingValues()
                  setIsAdding(false)
                  setFieldPanelKey(fieldPanelKey === rule.key ? null : rule.key)
                }}
                data-editor-panel-trigger
              >
                {rule.field}
                <Icon name="chevron-down" size={12} />
              </RawButton>
              <span className={cx(styles.select, styles.operator)}>is</span>
              <RawButton
                className={cx(
                  styles.select,
                  styles.value,
                  rule.isEditing && styles.selectOpen,
                )}
                aria-expanded={rule.isEditing}
                onPress={(e) => {
                  placePanel(e)
                  setFieldPanelKey(null)
                  setIsAdding(false)
                  if (rule.isEditing) {
                    onStopEditingValues()
                  } else {
                    onEditValue(rule.key)
                  }
                }}
                data-editor-panel-trigger
              >
                <span className={styles.valueText}>
                  {rule.valueText || 'Any'}
                </span>
                <Icon name="chevron-down" size={12} />
              </RawButton>
              <RawButton
                className={styles.remove}
                aria-label={`Remove ${rule.field} filter`}
                onPress={() => onRemove(rule.key)}
                testId={`job-list-saved-filter-remove-${rule.key}`}
              >
                <Icon name="close" size={14} />
              </RawButton>
              {fieldPanelKey === rule.key && (
                <SidePanel side={side}>
                  {fieldsFor(rule.key).map((field) => (
                    <DropdownItem
                      key={field.key}
                      onPress={() => {
                        setFieldPanelKey(null)
                        onChangeField(rule.key, field.key)
                      }}
                    >
                      {field.title}
                    </DropdownItem>
                  ))}
                </SidePanel>
              )}
              {rule.isEditing && (
                <SidePanel side={side}>{rule.valueEditor}</SidePanel>
              )}
            </div>
          ))}
          {addableFields.length > 0 && (
            <div className={styles.rule}>
              <RawButton
                className={cx(styles.add, isAdding && styles.addOpen)}
                aria-expanded={isAdding}
                onPress={(e) => {
                  placePanel(e)
                  onStopEditingValues()
                  setFieldPanelKey(null)
                  setIsAdding(!isAdding)
                }}
                testId="job-list-saved-filter-add"
                data-editor-panel-trigger
              >
                <Icon name="add" size={14} />
                Add filter
              </RawButton>
              {isAdding && (
                <SidePanel side={side}>
                  {addableFields.map((field) => (
                    <DropdownItem
                      key={field.key}
                      onPress={() => {
                        setIsAdding(false)
                        onAdd(field.key)
                      }}
                      testId={`job-list-saved-filter-add-${field.key}`}
                    >
                      {field.title}
                    </DropdownItem>
                  ))}
                </SidePanel>
              )}
            </div>
          )}
        </div>
      </div>
      <div className={styles.footer}>
        <span className={styles.count}>
          {jobCount !== null &&
            `${jobCount.toLocaleString()} ${jobCount === 1 ? 'job' : 'jobs'}`}
        </span>
        <Button
          label="Cancel"
          variant="text"
          size="small"
          onPress={onCancel}
          testId="job-list-saved-filter-cancel"
        />
        <Button
          label="Save"
          variant="primary"
          size="small"
          type="submit"
          isDisabled={!canSave}
          testId="job-list-saved-filter-save"
        />
      </div>
    </form>
  )
}

interface SidePanelProps {
  side: Side
  children: ReactNode
}

function SidePanel({ side, children }: SidePanelProps) {
  return (
    <div
      className={cx(styles.panel, side === 'left' && styles.panelLeft)}
      data-editor-panel
    >
      <DropdownItemList className={styles.panelList}>
        {children}
      </DropdownItemList>
    </div>
  )
}
